import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Load from '../../components/Load';
import TournamentTable from '../../components/admin/TournamentTable';
import { Tournament } from '../../components/admin/TournamentTableRow';
import {
  TORNEIOS_FILTRADOS,
  TorneiosFiltradosData,
  TorneiosFiltradosVars,
  ativarTorneio,
  inativarTorneio,
  deletarTorneio,
} from '../../services/torneioService';
import { useProfile } from '../../context/ProfileContext';
import { useAuth } from '../../context/AuthContext';
import { useLigaPermissions } from '../../hooks/useLigaPermissions';
import { Snackbar, Alert } from '@mui/material';
import { PageBreadcrumb } from '../../components/navigation';
import DuplicarTorneioDialog from '../../components/admin/DuplicarTorneioDialog';

const LeagueTournaments: React.FC = () => {
  const { currentProfile, availableProfiles, isLoading: profileLoading } = useProfile();
  const auth = useAuth();
  const usuarioId = (auth?.user as { id?: string })?.id;
  const navigate = useNavigate();
  const ligaId = currentProfile?.tipo === 'LIGA' ? currentProfile.referenciaId : null;

  const { canManageTournaments, isAdministrador, isLoading: permissionsLoading } = useLigaPermissions(ligaId);

  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateTarget, setDuplicateTarget] = useState<{ id: string; nome: string; username: string } | null>(null);

  const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  const { data, loading, error, refetch } = useQuery<
    TorneiosFiltradosData,
    TorneiosFiltradosVars
  >(TORNEIOS_FILTRADOS, {
    variables: { ligaId: ligaId!, page: 0, size: 1000 },
    skip: !ligaId || profileLoading || !currentProfile || currentProfile.tipo !== 'LIGA',
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (data?.torneiosFiltrados?.items && usuarioId) {
      const filtered = data.torneiosFiltrados.items.filter((t) => 
        t.organizadores?.some((org) => org.usuario?.id === usuarioId)
      );
      const mapped = filtered.map((t) => ({
        id: t.id,
        name: t.nome,
        username: t.username ?? '',
        avatar: t.avatarUrl || '/assets/images/tournament.png',
        city: t.local?.nome ?? '',
        state: '',
        startDate: t.dataInicio,
        active: t.ativo ?? false,
        stats: {
          totalCategorias: t.totalCategorias ?? 0,
          categoriasAtivas: t.categoriasAtivas ?? 0,
          totalInscricoes: t.totalInscricoes ?? 0,
          inscricoesNaoPagas: t.inscricoesNaoPagas ?? 0,
          totalPartidas: t.totalPartidas ?? 0,
          partidasSemResultado: t.partidasSemResultado ?? 0,
        },
      }));
      setTournaments(mapped);
    }
  }, [data, usuarioId]);

  // Se ainda está carregando o perfil, mostra loading
  if (profileLoading) {
    return (
      <>
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="row feed-body">
                <div className="col-xl-12">
                  <Load />
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Se não for perfil de liga, não renderiza nada
  if (!currentProfile || currentProfile.tipo !== 'LIGA') {
    return null;
  }

  // Verificar permissão de gerenciamento de torneios
  if (!canManageTournaments && !permissionsLoading) {
    return (
      <>
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="row feed-body">
                <div className="col-xl-12">
                  <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3 mt-3">
                    <div className="card-body p-4">
                      <div className="alert alert-warning" role="alert">
                        <h5 className="alert-heading">Acesso Negado</h5>
                        <p className="mb-0">
                          Você não tem permissão para gerenciar torneios desta Liga. Apenas
                          administradores e gerentes podem realizar esta ação.
                        </p>
                      </div>
                      <button
                        className="btn btn-primary mt-3"
                        onClick={() => navigate('/liga/dashboard')}
                      >
                        Voltar ao Dashboard
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (loading || permissionsLoading) {
    return (
      <>
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="row feed-body">
                <div className="col-xl-12">
                  <Load />
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="row feed-body">
                <div className="col-xl-12">
                  <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3 mt-3">
                    <div className="card-body p-4">
                      <div className="alert alert-danger" role="alert">
                        Erro ao carregar torneios. Tente novamente.
                      </div>
                      <button
                        className="btn btn-primary mt-3"
                        onClick={() => navigate('/liga/dashboard')}
                      >
                        Voltar ao Dashboard
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  const handleToggle = (id: string) => {
    if (!id) {
      console.error('ID do torneio é inválido');
      return;
    }

    const torneio = tournaments.find((t) => t.id === id);
    if (!torneio) return;

    const novoStatus = !torneio.active;

    if (novoStatus) {
      ativarTorneio(id)
        .then(() => {
          setTournaments((prev) =>
            prev.map((t) => (t.id === id ? { ...t, active: true } : t))
          );
          setSnackbar({
            open: true,
            message: 'Torneio ativado com sucesso!',
            severity: 'success',
          });
          refetch();
        })
        .catch(() => {
          setSnackbar({
            open: true,
            message: 'Erro ao ativar torneio. Tente novamente.',
            severity: 'error',
          });
        });
    } else {
      inativarTorneio(id)
        .then(() => {
          setTournaments((prev) =>
            prev.map((t) => (t.id === id ? { ...t, active: false } : t))
          );
          setSnackbar({
            open: true,
            message: 'Torneio inativado com sucesso!',
            severity: 'success',
          });
          refetch();
        })
        .catch(() => {
          setSnackbar({
            open: true,
            message: 'Erro ao inativar torneio. Tente novamente.',
            severity: 'error',
          });
        });
    }
  };

  const handleEdit = (id: string) => {
    navigate(`/liga/tournament/${id}/edit`);
  };

  const handleView = (id: string) => {
    navigate(`/liga/tournament/${id}`);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja deletar este torneio? Esta ação não pode ser desfeita.')) {
      return;
    }

    try {
      await deletarTorneio(id);
      setTournaments((prev) => prev.filter((t) => t.id !== id));
      setSnackbar({
        open: true,
        message: 'Torneio deletado com sucesso!',
        severity: 'success',
      });
      refetch();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Erro ao deletar torneio. Tente novamente.',
        severity: 'error',
      });
    }
  };

  const handleCreateNew = () => {
    navigate('/liga/tournament/new');
  };

  const handleDuplicate = (id: string) => {
    const torneio = tournaments.find((t) => t.id === id);
    if (torneio) {
      setDuplicateTarget({ id, nome: torneio.name, username: torneio.username ?? '' });
      setDuplicateDialogOpen(true);
    }
  };

  return (
    <>
      <Header />
      <div className="main-content right-chat-active">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left">
            <div className="row feed-body">
              <div className="col-xl-12">
                  <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3 mt-3">
                  <div className="card-body p-4">
                    <div className="mb-3">
                      <PageBreadcrumb
                        items={[
                          { label: 'Dashboard', href: '/liga/dashboard' },
                          { label: 'Torneios' },
                        ]}
                        onBack={() => navigate('/liga/dashboard')}
                        variant="bootstrap"
                      />
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h3 className="fw-700 mb-0">Torneios da Liga</h3>
                      {isAdministrador && (
                        <button
                          className="btn btn-primary"
                          onClick={handleCreateNew}
                        >
                          Criar Novo Torneio
                        </button>
                      )}
                    </div>

                    {loading ? (
                      <Load />
                    ) : tournaments.length === 0 ? (
                      <div className="alert alert-info" role="alert">
                        Nenhum torneio encontrado para esta liga.
                      </div>
                    ) : (
                      <TournamentTable
                        tournaments={tournaments}
                        onToggle={handleToggle}
                        onEdit={handleEdit}
                        onView={handleView}
                        onDelete={isAdministrador ? handleDelete : undefined}
                        onDuplicate={handleDuplicate}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {duplicateTarget && (
        <DuplicarTorneioDialog
          open={duplicateDialogOpen}
          onClose={() => setDuplicateDialogOpen(false)}
          torneioId={duplicateTarget.id}
          torneioNome={duplicateTarget.nome}
          torneioUsername={duplicateTarget.username}
        />
      )}
    </>
  );
};

export default LeagueTournaments;

